import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { Viewer } from '@/lib/access';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { TRAINING } from '../training/content';
import { ENTRIES } from './lib/allEntries';
import { audienceOf, entriesFor } from './lib/audience';
import { formatEntryDate } from './lib/entries';
import { WHATS_NEW_LINK_PARAM, hasWhatsNewLink, takeWhatsNewLink } from './lib/link';
import { browserStorage, readSeen, seenKey, unreadIds, writeSeen } from './lib/seen';
import type { WhatsNewEntry } from './types';

/**
 * The header's "What's new" button and the drawer it opens: recent changes for the signed-in
 * person's role, each linked to the part of the Training guide that covers it.
 *
 * ⚠️ `me` IS ONLY EVER READ, NEVER FETCHED HERE. It comes from the shell, which already waits
 * for it, so the button costs no request. Until it arrives nothing is drawn: showing the
 * unfiltered list for a moment would tell a field leader about a screen they cannot open.
 *
 * Opening the drawer (or following an email's `?whatsnew=1` link) marks everything as seen, so
 * the number clears at once; the entries that were new stay tagged "New" until it is closed.
 */
export type WhatsNewMe = (Viewer & { id: string; impersonating?: boolean }) | undefined;

/** How many entries the drawer shows before "Show older updates". */
const PAGE = 12;

const pad = (n: number) => String(n).padStart(2, '0');
/** Today's calendar date in the browser's own zone: the day the person is looking at. */
const isoToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const css = `
  .wn-btn {
    display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
    height: 26px; padding: 0 10px; border-radius: var(--ew-r-pill);
    background: transparent; color: rgba(255,255,255,0.75);
    border: 1px solid rgba(255,255,255,0.25); cursor: pointer;
    font: var(--ew-fw-medium) var(--ew-fs-meta)/1 var(--ew-font);
    transition: color var(--ew-dur-fast) var(--ew-ease), border-color var(--ew-dur-fast) var(--ew-ease), background var(--ew-dur-fast) var(--ew-ease);
  }
  .wn-btn:hover { color: #fff; border-color: var(--ew-green); background: rgba(2,120,53,0.18); }
  /* The header already drops the product name and the user's name at this width (App.tsx), so the label goes too: the button
     is the sparkle and the number, and its accessible name is unchanged. Otherwise the fixed-width controls overflow a phone. */
  @media (max-width: 768px) {
    .wn-label { display: none; }
    .wn-btn { padding: 0 8px; }
  }
  .wn-badge {
    display: inline-flex; align-items: center; justify-content: center; min-width: 16px; height: 16px; padding: 0 5px;
    border-radius: var(--ew-r-pill); background: var(--ew-green); color: #fff; font: var(--ew-fw-semibold) 10.5px/1 var(--ew-font);
  }
  .wn-scrim { position: fixed; inset: 0; background: rgba(0,0,0,0.25); z-index: 300; }
  .wn-drawer {
    position: fixed; top: 0; right: 0; bottom: 0; width: 440px; max-width: 92vw; z-index: 301;
    background: var(--ew-white); box-shadow: var(--ew-shadow-3); display: flex; flex-direction: column;
    font-family: var(--ew-font);
  }
  .wn-head { display: flex; align-items: center; gap: var(--ew-s-3); padding: var(--ew-s-4) var(--ew-s-5); border-bottom: 1px solid var(--ew-n-200); flex-shrink: 0; }
  .wn-head h2 { flex: 1; margin: 0; font: var(--ew-fw-semibold) var(--ew-fs-h4)/1.2 var(--ew-font); color: var(--ew-dark); }
  .wn-x {
    width: 30px; height: 30px; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center;
    border-radius: var(--ew-r-pill); border: 1px solid var(--ew-n-300); background: var(--ew-white);
    color: var(--ew-n-500); font-size: 16px; line-height: 1; cursor: pointer;
  }
  .wn-x:hover { color: var(--ew-dark); border-color: var(--ew-dark); }
  .wn-body { flex: 1; overflow-y: auto; padding: var(--ew-s-5); }
  .wn-intro { margin: 0 0 var(--ew-s-4); font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-body) var(--ew-font); color: var(--ew-n-700); }
  .wn-empty { margin: 0; font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-body) var(--ew-font); color: var(--ew-n-500); }
  .wn-entry { padding: var(--ew-s-4) 0; border-top: 1px solid var(--ew-n-100); }
  .wn-entry:first-of-type { border-top: 0; padding-top: 0; }
  .wn-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 4px; font: var(--ew-fw-medium) var(--ew-fs-meta)/1.3 var(--ew-font); color: var(--ew-n-500); }
  .wn-for { color: var(--ew-n-500); }
  .wn-new { padding: 2px 7px; border-radius: var(--ew-r-pill); background: var(--ew-green); color: #fff; font: var(--ew-fw-semibold) 10.5px/1.2 var(--ew-font); letter-spacing: .03em; text-transform: uppercase; }
  .wn-title { margin: 0 0 4px; font: var(--ew-fw-semibold) var(--ew-fs-body)/1.3 var(--ew-font); color: var(--ew-dark); }
  .wn-summary { margin: 0; font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-body) var(--ew-font); color: var(--ew-n-700); }
  .wn-shot { display: block; width: 100%; height: auto; margin-top: var(--ew-s-3); border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); }
  .wn-shot-btn { display: block; width: 100%; margin-top: var(--ew-s-3); padding: 0; border: 0; background: none; cursor: zoom-in; text-align: left; font-family: var(--ew-font); }
  .wn-shot-btn .wn-shot { margin-top: 0; }
  .wn-shot-btn:hover .wn-shot { border-color: var(--ew-n-500); }
  .wn-shot-hint { display: block; margin-top: 4px; font: var(--ew-fw-medium) var(--ew-fs-meta)/1.3 var(--ew-font); color: var(--ew-n-500); }
  .wn-zoom { position: fixed; inset: 0; z-index: 302; display: flex; align-items: center; justify-content: center; padding: 24px; background: rgba(0,0,0,0.72); cursor: zoom-out; }
  .wn-zoom-img { max-width: 100%; max-height: 100%; width: auto; height: auto; background: var(--ew-white); border-radius: var(--ew-r-md); box-shadow: var(--ew-shadow-3); }
  .wn-zoom-x { position: absolute; top: 16px; right: 16px; }
  .wn-details { margin: var(--ew-s-3) 0 0; padding-left: 18px; font: var(--ew-fw-regular) var(--ew-fs-body-sm)/var(--ew-lh-body) var(--ew-font); color: var(--ew-n-700); }
  .wn-details li + li { margin-top: 4px; }
  .wn-links { display: flex; flex-wrap: wrap; gap: 6px 16px; margin-top: var(--ew-s-3); font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.3 var(--ew-font); }
  .wn-links a { color: #15659E; text-decoration: none; }
  .wn-links a:hover { text-decoration: underline; }
  .wn-more { margin-top: var(--ew-s-3); padding: 8px 14px; border-radius: var(--ew-r-md); border: 1px solid var(--ew-n-300); background: #fff; color: var(--ew-dark); font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1 var(--ew-font); cursor: pointer; }
  .wn-more:hover { border-color: var(--ew-dark); }
  .wn-foot { flex-shrink: 0; padding: var(--ew-s-3) var(--ew-s-5); border-top: 1px solid var(--ew-n-200); background: var(--ew-n-50); font: var(--ew-fw-regular) var(--ew-fs-meta)/1.5 var(--ew-font); color: var(--ew-n-700); }
  .wn-foot a { color: #15659E; text-decoration: none; font-weight: var(--ew-fw-medium); }
  .wn-foot a:hover { text-decoration: underline; }
`;

type Shot = { src: string; alt: string };

function Entry({ entry, isNew, onFollow, onZoom }: {
  entry: WhatsNewEntry;
  isNew: boolean;
  onFollow: () => void;
  onZoom: (shot: Shot) => void;
}) {
  return (
    <article className="wn-entry">
      <div className="wn-meta">
        <span>{formatEntryDate(entry.date)}</span>
        {entry.audience && <span className="wn-for">For {entry.audience.label}</span>}
        {isNew && <span className="wn-new">New</span>}
      </div>
      <h3 className="wn-title">{entry.title}</h3>
      <p className="wn-summary">{entry.summary}</p>
      {entry.image && (
        // The drawer is narrow, so a screenshot is shrunk to about 400px and its text becomes unreadable: clicking opens it full size.
        <button
          type="button"
          className="wn-shot-btn"
          aria-label={`Enlarge screenshot: ${entry.image.alt}`}
          onClick={() => onZoom({ src: `/whats-new/${entry.image!.file}`, alt: entry.image!.alt })}
        >
          <img className="wn-shot" src={`/whats-new/${entry.image.file}`} alt={entry.image.alt} loading="lazy" />
          <span className="wn-shot-hint">Click to enlarge</span>
        </button>
      )}
      {entry.details && (
        <ul className="wn-details">
          {entry.details.map((d) => <li key={d}>{d}</li>)}
        </ul>
      )}
      {(entry.tryIt || entry.guide) && (
        <div className="wn-links">
          {entry.tryIt && <Link to={entry.tryIt.to} onClick={onFollow}>{entry.tryIt.label} →</Link>}
          {entry.guide && (
            <Link to={`/training?topic=${encodeURIComponent(entry.guide.topic)}`} onClick={onFollow}>
              What changed in the guide →
            </Link>
          )}
        </div>
      )}
    </article>
  );
}

/**
 * The drawer itself, mounted only while it is open so that its focus trap starts and ends with it.
 *
 * ⚠️ BOTH LAYERS DECLARE `aria-modal`, SO BOTH TRAP TAB (the shared `useFocusTrap`): without it Tab walks off the last link into
 * the header and the page behind a dimmed screen. `paused` hands Tab and Escape to the enlarged screenshot while it is on top,
 * because the trap listens on the whole document and both would otherwise answer the same key. The trap also puts focus on the
 * first control when it opens and gives it back to whatever had it (the header button, or the screenshot that was clicked) when
 * it closes.
 */
function Drawer({ shown, hasMore, fresh, paused, onClose, onMore, onZoom }: {
  shown: readonly WhatsNewEntry[];
  hasMore: boolean;
  fresh: ReadonlySet<string>;
  paused: boolean;
  onClose: () => void;
  onMore: () => void;
  onZoom: (shot: Shot) => void;
}) {
  const root = useRef<HTMLElement>(null);
  useFocusTrap(root, { onEscape: onClose, paused });
  return (
    <aside ref={root} className="wn-drawer" role="dialog" aria-modal="true" aria-label="What’s new">
      <div className="wn-head">
        <h2>What’s new</h2>
        <button className="wn-x" onClick={onClose} aria-label="Close What’s new">✕</button>
      </div>
      <div className="wn-body">
        <p className="wn-intro">Recent changes to the Project Tracker that apply to you, newest first.</p>
        {shown.length === 0
          ? <p className="wn-empty">Nothing new for your role yet.</p>
          : shown.map((e) => <Entry key={e.id} entry={e} isNew={fresh.has(e.id)} onFollow={onClose} onZoom={onZoom} />)}
        {hasMore && <button className="wn-more" onClick={onMore}>Show older updates</button>}
      </div>
      <div className="wn-foot">
        Training guide: revision {TRAINING.revision}, reviewed {formatEntryDate(TRAINING.revised)}.{' '}
        <Link to="/training" onClick={onClose}>Open the guide →</Link>
      </div>
    </aside>
  );
}

/** A screenshot at full size, over the drawer. Its own trap, and the drawer's is paused while it is open. */
function Zoom({ shot, onClose }: { shot: Shot; onClose: () => void }) {
  const root = useRef<HTMLDivElement>(null);
  useFocusTrap(root, { onEscape: onClose });
  return (
    <div ref={root} className="wn-zoom" role="dialog" aria-modal="true" aria-label="Screenshot" onClick={onClose}>
      <button className="wn-x wn-zoom-x" onClick={onClose} aria-label="Close screenshot">✕</button>
      <img className="wn-zoom-img" src={shot.src} alt={shot.alt} />
    </div>
  );
}

/**
 * `entries` and `today` are the tests' way in; the app passes neither.
 */
export default function WhatsNew({ me, entries = ENTRIES, today }: {
  me: WhatsNewMe;
  entries?: readonly WhatsNewEntry[];
  today?: string;
}) {
  const [params, setParams] = useSearchParams();
  const userId = me?.id ?? null;
  const mine = useMemo(() => (me ? entriesFor(entries, audienceOf(me)) : []), [entries, me]);

  // ⚠️ WORKED OUT DURING RENDER, NOT IN AN EFFECT. The header mounts before `me` arrives, and an effect would leave the first
  // render, and an email link that opens the drawer in the same commit, counting from "nothing stored" for someone who has
  // seen everything. `opened` is what this page has marked seen since; it is keyed by person because "View as" swaps `me`
  // under a mounted header, and what one person has seen is not what the next has.
  //
  // ⚠️ AND IT IS RE-READ WHEN ANOTHER TAB WRITES IT. `localStorage` is shared by every tab of the app, and a tab that opened the
  // drawer has already marked everything seen; this tab would keep its badge, and tag the same entries New, until it was reloaded.
  // The browser fires `storage` in the OTHER tabs only, so `version` is bumped from that and nothing else.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!userId) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === seenKey(userId)) setVersion((v) => v + 1);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` is the trigger: nothing it reads changes
  const stored = useMemo(() => (userId ? readSeen(browserStorage(), userId) : null), [userId, version]);
  const [opened, setOpened] = useState<{ userId: string; seen: Set<string> } | null>(null);
  const seen = opened && opened.userId === userId ? opened.seen : stored;

  const unread = useMemo(() => unreadIds(mine, seen, today ?? isoToday()), [mine, seen, today]);

  const [open, setOpen] = useState(false);
  const [fresh, setFresh] = useState<ReadonlySet<string>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const [zoomed, setZoomed] = useState<Shot | null>(null);

  const openDrawer = () => {
    if (!me) return;
    // Read it again rather than trust the render's copy: another tab may have opened the drawer since, and the entries it
    // marked seen must not be tagged New here. What this page marked itself is only ever more, so the two are joined.
    const latest = readSeen(browserStorage(), me.id);
    const known = opened && opened.userId === me.id ? new Set([...opened.seen, ...(latest ?? [])]) : latest;
    setFresh(new Set(unreadIds(mine, known, today ?? isoToday())));
    // EVERY entry, not just this person's: if they later gain an access that makes an older entry theirs, it must not
    // come back as news. Anything `seen` holds that no longer exists is dropped, so the list cannot grow without end.
    const next = new Set(entries.map((e) => e.id));
    setOpened({ userId: me.id, seen: next });
    // "View as" is read-only and the stored state would be somebody else's: look, but leave nothing behind.
    if (!me.impersonating) writeSeen(browserStorage(), me.id, next);
    setShowAll(false);
    setOpen(true);
  };
  const close = () => {
    setOpen(false);
    setZoomed(null);
  };

  // An email's "?whatsnew=1" link opens the drawer, then takes the parameter off so a refresh does not reopen it. If the person had
  // to sign in first, the sign-in screen remembered the link (see lib/link), and this takes it once `me` is here.
  const inUrl = hasWhatsNewLink(params.toString());
  useEffect(() => {
    if (!me || !takeWhatsNewLink(params.toString())) return;
    openDrawer();
    if (inUrl) {
      const rest = new URLSearchParams(params);
      rest.delete(WHATS_NEW_LINK_PARAM);
      setParams(rest, { replace: true });
    }
    // `openDrawer` closes over this render's state and is meant to run once per link.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inUrl, userId]);

  if (!me) return null;
  const shown = showAll ? mine : mine.slice(0, PAGE);

  return (
    <>
      <style>{css}</style>
      <button
        className="wn-btn"
        onClick={openDrawer}
        title="What’s new in the Project Tracker, and in the training guide"
        aria-label={unread.length > 0 ? `What’s new, ${unread.length} you haven’t seen` : 'What’s new'}
      >
        <span aria-hidden="true">✨</span>
        <span className="wn-label">What’s new</span>
        {unread.length > 0 && <span className="wn-badge">{unread.length}</span>}
      </button>

      {open && (
        <>
          <div className="wn-scrim" onClick={close} />
          <Drawer
            shown={shown}
            hasMore={!showAll && mine.length > PAGE}
            fresh={fresh}
            paused={zoomed !== null}
            onClose={close}
            onMore={() => setShowAll(true)}
            onZoom={setZoomed}
          />
          {zoomed && <Zoom shot={zoomed} onClose={() => setZoomed(null)} />}
        </>
      )}
    </>
  );
}
